import { useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { toSpeechText } from '../utils/ttsText';

// Module-level: only one utterance plays at a time across all TTSButton instances
let stopActive: (() => void) | null = null;

interface Props {
  text: string;
  className?: string;
}

export default function TTSButton({ text, className = '' }: Props) {
  const [speaking, setSpeaking] = useState(false);

  const toggle = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      stopActive = null;
      return;
    }

    stopActive?.();

    const u = new SpeechSynthesisUtterance(toSpeechText(text));

    const stop = () => {
      synth.cancel();
      setSpeaking(false);
    };
    const cleanup = () => {
      setSpeaking(false);
      if (stopActive === stop) stopActive = null;
    };

    u.onend = cleanup;
    u.onerror = cleanup;
    stopActive = stop;
    setSpeaking(true);
    synth.speak(u);
  };

  if (!window.speechSynthesis) return null;

  return (
    <button
      onClick={toggle}
      title={speaking ? 'Stop reading' : 'Listen to this'}
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-medium text-xs transition-colors flex-shrink-0 ${
        speaking
          ? 'bg-coral-warm border-coral-warm text-white'
          : 'bg-orange-50 border-orange-200 text-coral-warm hover:bg-orange-100'
      } ${className}`}
    >
      {speaking && <span className="absolute inset-0 rounded-full bg-coral-warm opacity-60 animate-ping" />}
      {speaking ? <VolumeX className="w-4 h-4 relative" /> : <Volume2 className="w-4 h-4 relative" />}
      <span className="relative">{speaking ? 'Stop' : 'Listen'}</span>
    </button>
  );
}
